/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { PageState } from 'models/PageState';
import { Children, ReactNode, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

type ExperimentFunc = (isRunning: boolean, flag?: number) => JSX.Element;
interface Props {
  test: string;
  children: ReactNode | ExperimentFunc;
  default?: number;
  variant?: number;
}

export const getExperiments = (state: PageState) => state?.currentPage?.meta?.experiments;
interface GetIsExperimentProps {
  experiments: [string, number][];
  experiment: Pick<Props, 'test'>;
}

export const getIsExperiment = ({ experiments, experiment }: GetIsExperimentProps) => {
  return !!experiments.find((_experiment: [string, number]) => {
    const isExperiment = _experiment[0] === experiment?.test;
    const isActive = _experiment[1] > 0;
    return isExperiment && isActive;
  });
};

export function experimentCheck(e: unknown): e is [string, number][] {
  const TUPLE_SIZE = 2;
  return Array.isArray(e) && e.length > 0 && e[0].length === TUPLE_SIZE;
}

const EXCLUDED = -1;
const DEFAULT = 0;
const findExperiment = (experiments: unknown, test: string): [string, number] =>
  (experimentCheck(experiments) && Object.values(experiments).find(([key]) => test === key)) || [
    '',
    EXCLUDED
  ];

interface UseExperimentProps {
  experiment?: { test: string };
}

interface UseExperimentReturnValues {
  isRunning: boolean;
  variant: number;
}

export const useExperiment = ({ experiment }: UseExperimentProps): UseExperimentReturnValues => {
  const experiments = useSelector(getExperiments);
  const test = experiment?.test || '';
  const [, active] = findExperiment(experiments, test);

  return {
    isRunning: active > 0,
    variant: active
  };
};

const isExperimentFunction = (children: unknown): children is ExperimentFunc =>
  typeof children === 'function';

export const Experiment = (props: Props): JSX.Element => {
  const { children, test } = props;
  const def = props.default || DEFAULT;
  const [{ isRunning, variant }] = useState(useExperiment({ experiment: { test } }));
  return useMemo(() => {
    if (isExperimentFunction(children)) {
      return children(isRunning, variant);
    }
    const [a, b] = Children.toArray(props.children as ReactNode);
    return <>{variant <= def ? a : b}</>;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
